<!--

Slots:
------

#default
обёрнут в fieldset, меняющий состояние disabled

#unlocked
снаружи fieldset, смена состояния редактирования не влияет

#pre-controls
внутри нижнего блока кнопок, перед первой общей кнопокй, не учитывает состояние формы (редактирование вкл/выкл)

#controls
внутри нижнего блока кнопок, после общих кнопок, учитывает состояние формы (редактирование вкл/выкл)

#post-controls
внитри нижнего блока кнопок, после блока #controls, не учитывает состояние формы (редактирование вкл/выкл)

 -->

<template>
  <DfnControlTpl>
    <slot name="pre-controls" />
    <template v-if="entity.id && canEdit && havePermissions && isVisible && !isDeleted">
      <a
        v-if="!isEditing && (isLockNone || isLockedByMe) && canEdit"
        href="#"
        class="btn btn-outline-primary"
        @click.prevent="edit"
      >
        <x-icon icon="pencil" />
        {{ thisEditLabel }}
      </a>
      <a
        v-if="isEditing"
        href="#"
        class="btn btn-outline-primary"
        @click.prevent="reset"
      >
        <x-icon icon="pencil" />
        Выйти из редактирования
      </a>
      <a
        v-if="isLockedByOthers"
        href="#"
        class="btn btn-outline-danger"
        @click.prevent="reset"
      >
      <x-icon icon="lock-open"/>
        Сбросить блокировку
      </a>
    </template>
    <slot
      v-if="!isEditing"
      name="create-act-pp"
    />
    <template v-if="isEditing && havePermissions && canEdit">
      <button
        v-if="canSave"
        class="btn btn-outline-primary"
        type="submit"
        :form="`form-${formid}`"
      >
        <x-icon icon="check" />
        Сохранить
      </button>
    </template>

    <slot
      v-if="isEditing"
      name="controls"
    />
    <slot
      v-if="!isEditing"
      name="controls-rev"
    />
    <slot name="post-controls" />
  </DfnControlTpl>

  <form
    @submit.stop.capture.prevent="onSubmit"
    :id="`form-${formid}`"
    class="form-blockable"
  >
    <BlockScreen v-if="blockScreen" />
    <fieldset :disabled="!isEditing">
      <slot
        :editable="isEditing"
        :errors="errors"
      />
    </fieldset>
    <slot name="unlocked" />
    <div
      v-if="isLockedByOthers"
      class="alert alert-info"
    >
      <i class="bi bi-lock" />
      Редактирование невозможно: пользователь <b>{{ lockState.lock.author.username }}</b> уже
      редактирует эту запись
    </div>
    <div
      v-if="!havePermissions"
      class="alert alert-info mt-3"
    >
      Редактирование невозможно. У вас нет прав
    </div>
    <div class="hstack mt-3 gap-2 align-items-center flex-wrap">
      <template v-if="stickyControls">
        <Stickypanel>
          <ReControlTpl />
        </Stickypanel>
      </template>
      <template v-else>
        <ReControlTpl />
      </template>
    </div>
  </form>
</template>
<script setup>
import { onUnmounted, watch, provide, computed, onMounted, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import { v4 as uuid4 } from 'uuid'
import { createReusableTemplate } from '@vueuse/core'
import config from '@/config'
import { useApi } from '@provise/composables/api'
import { useLockStore } from '@provise/stores/lock'
import { useUserStore } from '@provise/stores/user.js'
import { useUiStore } from '@provise/stores/ui.js'
import BlockScreen from '@provise/components/blockscreen.vue'
import Stickypanel from './stickypanel.vue'

const props = defineProps({
  entityName: {
    type: String,
    required: true,
  },
  entityRbac: {
    type: Array,
    default: () => ['common'],
  },
  editPermission: {
    type: Boolean,
    default: true,
  },
  entity: {
    type: Object,
    required: true,
  },
  // edit button eabled (true)/disabled(false)
  canEdit: {
    type: Boolean,
    default: true,
  },
  editLabel: {
    type: String,
    required: false,
  },
  isVisible: {
    type: Boolean,
    default: true,
  },
  canSave: {
    type: Boolean,
    default: true,
  },
  stickyControls: Boolean,
  rules: Array,
})
const emits = defineEmits(['form:reset', 'form:apply', 'form:editable'])
defineExpose({
  reset,
  edit,
})

const lockStatePollInterval = Number(config.VITE_LOCK_INTERVAL ?? 5000)

const formid = uuid4()
const [DfnControlTpl, ReControlTpl] = createReusableTemplate()
const userStore = useUserStore()
const { lockKeys } = storeToRefs(useLockStore())
const { blockScreen } = storeToRefs(useUiStore())
const lockStateQuery = computed(() => ({ entity_name: props.entityName, id: props.entity.id }))
const entityPath = computed(() =>
  props.entity?.id ? `${props.entityName}/${props.entity.id}` : null
)
const { model: lockState, refresh: fetchLockState } = useApi({
  url: 'lock_check',
  query: lockStateQuery,
  immediate: true,
  before: () => Boolean(props.entity?.id),
})
const { refresh: lockSet, model: lockItem } = useApi({
  url: 'lock',
  query: lockStateQuery,
})
const { refresh: lockReset } = useApi({
  url: 'lock_reset',
  query: lockStateQuery,
})

let poll
const lk = computed({
  get() {
    return lockKeys.value[entityPath.value] ?? null
  },
  set(val) {
    if (entityPath.value === null) {
      return
    }
    const keys = { ...lockKeys.value }
    if (val === undefined) {
      delete keys[entityPath.value]
    } else {
      keys[entityPath.value] = val
    }

    lockKeys.value = keys
  },
})
const isLockNone = computed(() =>
  lockState.value ? lockState.value.success === true && lockState.value.lock === null : null
)
const isLockedByOthers = computed(
  () =>
    lockState.value?.success === false
    && lockState.value.lock !== null
    && lockState.value.lock.lock_key !== lk.value
)
const isLockedByMe = computed(
  () => lockState.value?.success === true && lockState.value.lock?.lock_key === lk.value
)
const isEditing = computed(() => (props.entity?.id ? lk.value !== null : true))

const havePermissions = computed(() => userStore.can(props.entityRbac) && props.editPermission)
const thisEditLabel = computed(() => props.editLabel ?? 'Редактировать')
const isDeleted = computed(() => props.entity?.deleted === true)

provide('form:editable', isEditing)

watch(isLockNone, (val, prev) => {
  // refresh all form instance after someone edited model
  if (prev === false && val === true) {
    emits('form:reset')
  }
})

watch(
  () => props.entity?.id,
  async id => {
    if (!id) {
      lockPollStop()
      return
    }
    await lockPoll()
    lockPollStart()
  },
  { immediate: true }
)

watch(
  lockState,
  ls => {
    if (!ls) {
      return
    }
    if ((ls.success === true && ls.lock === null) || ls.success === false) {
      lk.value = undefined
    }
  },
  { immediate: true }
)
watch(lockItem, l => {
  if (l.success === true) {
    lk.value = l.lock?.lock_key
  } else {
    lk.value = undefined
  }
})
watch(
  isLockedByMe,
  l => {
    if (l === false) {
      lk.value = undefined
    }
  },
  {
    immediate: true,
  }
)

watch(isEditing, isEditing => {
  emits('form:editable', isEditing)
})

onMounted(() => {
  window.addEventListener('beforeunload', unload)
})

onUnmounted(async () => {
  lockPollStop()
  window.removeEventListener('beforeunload', unload)
})

onBeforeRouteLeave(beforeRouteChanges)
onBeforeRouteUpdate(beforeRouteChanges)

function lockPollStart() {
  lockPollStop()
  if (lockStatePollInterval !== -1) {
    poll = setInterval(lockPoll, lockStatePollInterval)
  }
}

function lockPollStop() {
  if (poll === undefined) {
    return
  }

  clearInterval(poll)
  poll = undefined
}

async function lockPoll() {
  let fls = {}
  if (lk.value) {
    fls.headers = {
      'X-Api-Lock': lk.value,
    }
  }
  await fetchLockState(fls)
}

async function reset() {
  await lockReset()
  lk.value = undefined
  await lockPoll()
}

async function edit() {
  if (!props.canEdit) {
    return
  }
  await lockSet()
  await lockPoll()
}

async function fullReset() {
  await lockReset()
  lk.value = undefined
  lockPollStop()
}

async function unload() {
  if (isLockedByMe.value) {
    await fullReset()
  }
}

const errors = reactive({})

const validate = () => {
  let valid = true
  Object.keys(errors).forEach(key => {
    errors[key] = null
  })

  props.rules.forEach(rule => {
    let value = props.entity[rule.name]
    if (rule.type === 'object') {
      value = props.entity[rule.name]?.id
    }

    if (rule.required && !value) {
      valid = false
      errors[rule.name] = rule.message
    }
  })

  return valid
}

const onSubmit = () => {
  if (!props.canSave || blockScreen.value) {
    return
  }
  blockScreen.value = true
  if (props.rules) {
    if (validate()) {
      emits('form:apply')
    } else {
      blockScreen.value = false
    }
  } else {
    emits('form:apply', () => (blockScreen.value = false))
  }
}

async function beforeRouteChanges() {
  if (!isLockedByMe.value) {
    lockPollStop()
    return true
  }
  if (!confirm('Изменения не сохранятся. Продолжить?')) {
    return false
  }

  lockPollStop()
  if (isLockedByMe.value) {
    await fullReset()
  }
}

if (props.rules) {
  props.rules.forEach(rule => {
    watch(
      () => props.entity[rule.name],
      () => {
        if (errors[rule.name]) {
          errors[rule.name] = null
        }
      }
    )
  })
}
</script>
